/**
 * Smart Billing™ — Patient Billing Tab
 * Financial-style premium dashboard inside ClientProfile.
 *
 * Sections:
 *  • Hero KPI strip (paid, due, paid this year, # receipts)
 *  • Suggested treatments banner (uninvoiced appointments)
 *  • Payments list with status pills, methods, PDF download, partial top-up
 *  • New payment modal with line items editor & live total
 *
 * Architecture: SDI-ready data model is stored server-side. Frontend stays
 * agnostic to fiscal provider, only renders status badges.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../ui/dialog";
import {
  Wallet, TrendingUp, Clock, FileText, Plus, Download, Loader2,
  AlertCircle, CheckCircle2, MinusCircle, Ban, Trash2, Sparkles,
  CreditCard, Banknote, Building2, Receipt, X, ChevronRight,
  Upload, Paperclip, ExternalLink,
} from "lucide-react";

const fmtEUR = (n) => `€ ${Number(n || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" }) : "—";

const STATUS = {
  paid:     { label: "Pagato",     icon: CheckCircle2, cls: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300", dot: "bg-emerald-500" },
  partial:  { label: "Parziale",   icon: MinusCircle,  cls: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",       dot: "bg-amber-500" },
  pending:  { label: "In sospeso", icon: AlertCircle,  cls: "bg-slate-100 text-slate-700 dark:bg-slate-700/60 dark:text-slate-300",        dot: "bg-slate-400" },
  refunded: { label: "Rimborsato", icon: MinusCircle,  cls: "bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300",            dot: "bg-rose-500" },
  voided:   { label: "Annullato",  icon: Ban,          cls: "bg-slate-200 text-slate-500 line-through dark:bg-slate-800",                  dot: "bg-slate-300" },
};

const METHOD_ICONS = {
  cash:          Banknote,
  card:          CreditCard,
  bank_transfer: Building2,
  pos:           CreditCard,
  stripe:        CreditCard,
  check:         FileText,
  other:         Wallet,
};

const BillingTab = ({ clientId, clientName }) => {
  const [summary,  setSummary]  = useState(null);
  const [payments, setPayments] = useState([]);
  const [methods,  setMethods]  = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [creating, setCreating] = useState(false);
  const [openNew, setOpenNew]   = useState(false);
  const [editing, setEditing]   = useState(null);    // payment object for top-up
  const [seedItems, setSeedItems] = useState([]);    // from suggestions

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sR, pR, mR, suR] = await Promise.all([
        axios.get(`${API}/dental/billing/summary/${clientId}`, { withCredentials: true }),
        axios.get(`${API}/dental/billing/payments?client_id=${clientId}`, { withCredentials: true }),
        axios.get(`${API}/dental/billing/methods`, { withCredentials: true }),
        axios.get(`${API}/dental/billing/suggestions/treatments/${clientId}`, { withCredentials: true }),
      ]);
      setSummary(sR.data);
      setPayments(pR.data.payments || []);
      setMethods(mR.data.methods || []);
      setSuggestions(suR.data.suggestions || []);
    } catch (e) {
      toast.error("Errore caricamento billing");
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  const openNewPayment = (items = []) => {
    setSeedItems(items);
    setOpenNew(true);
  };

  if (loading && !summary) {
    return (
      <div className="space-y-4" data-testid="billing-tab-loading">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[0,1,2,3].map(i => <div key={i} className="h-24 rounded-2xl bg-slate-100 dark:bg-white/5 animate-pulse" />)}
        </div>
        <div className="h-48 rounded-2xl bg-slate-100 dark:bg-white/5 animate-pulse" />
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="billing-tab">
      {/* HERO STRIP */}
      <Hero summary={summary} clientName={clientName} onNew={() => openNewPayment([])} />

      {/* SUGGESTIONS */}
      {suggestions.length > 0 && (
        <SuggestionsBanner
          suggestions={suggestions}
          onPickAll={() => openNewPayment(suggestions)}
          onPickOne={(s) => openNewPayment([s])}
        />
      )}

      {/* PAYMENTS LIST */}
      <PaymentsList
        payments={payments}
        methods={methods}
        onTopUp={(p) => setEditing(p)}
        onChanged={load}
      />

      {/* DIALOGS */}
      <NewPaymentDialog
        open={openNew}
        onOpenChange={(v) => { setOpenNew(v); if (!v) setSeedItems([]); }}
        clientId={clientId}
        methods={methods}
        seedItems={seedItems}
        onCreated={() => { setOpenNew(false); setSeedItems([]); load(); }}
        creating={creating}
        setCreating={setCreating}
      />

      <TopUpDialog
        payment={editing}
        methods={methods}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); load(); }}
      />
    </div>
  );
};

/* ============================================================ */
/* HERO                                                         */
/* ============================================================ */
const Hero = ({ summary, clientName, onNew }) => {
  const s = summary || {};
  return (
    <div className="rounded-3xl border border-slate-200/60 dark:border-white/10 bg-gradient-to-br from-violet-500/5 via-transparent to-cyan-500/5 dark:from-violet-500/10 dark:to-cyan-500/10 p-5">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div>
          <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-violet-500">Smart Billing</p>
          <h3 className="text-lg sm:text-xl font-black text-slate-900 dark:text-white tracking-tight">Dashboard economica</h3>
        </div>
        <Button
          onClick={onNew}
          className="rounded-full bg-gradient-to-r from-violet-600 to-cyan-500 hover:opacity-90 text-white shadow-lg shadow-violet-500/20"
          data-testid="billing-new-payment-btn"
        >
          <Plus className="w-4 h-4 mr-1.5" /> Nuovo pagamento
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
        <Kpi
          icon={CheckCircle2} label="Pagato totale" value={fmtEUR(s.total_paid)}
          accent="emerald" testid="kpi-paid"
        />
        <Kpi
          icon={AlertCircle}  label="Da saldare"    value={fmtEUR(s.total_due)}
          accent={s.total_due > 0 ? "amber" : "slate"} testid="kpi-due"
        />
        <Kpi
          icon={TrendingUp}   label={`Anno ${s.year || ""}`} value={fmtEUR(s.paid_this_year)}
          accent="violet" testid="kpi-year"
        />
        <Kpi
          icon={Receipt}      label="Ricevute" value={s.count_receipts || 0}
          accent="cyan" testid="kpi-receipts"
        />
      </div>
    </div>
  );
};

const ACCENTS = {
  emerald: "from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 text-emerald-600 dark:text-emerald-300",
  amber:   "from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 text-amber-600 dark:text-amber-300",
  violet:  "from-violet-50 to-purple-50 dark:from-violet-900/20 dark:to-purple-900/20 text-violet-600 dark:text-violet-300",
  cyan:    "from-cyan-50 to-sky-50 dark:from-cyan-900/20 dark:to-sky-900/20 text-cyan-600 dark:text-cyan-300",
  slate:   "from-slate-50 to-slate-100 dark:from-white/5 dark:to-white/10 text-slate-500",
};

const Kpi = ({ icon: Icon, label, value, accent = "slate", testid }) => (
  <div className={`rounded-2xl border border-slate-200/60 dark:border-white/10 bg-gradient-to-br ${ACCENTS[accent]} p-3.5`} data-testid={testid}>
    <div className="flex items-center gap-1.5">
      <Icon className="w-3.5 h-3.5 opacity-80" />
      <p className="text-[9.5px] uppercase tracking-wider font-bold opacity-80">{label}</p>
    </div>
    <p className="font-black text-slate-900 dark:text-white text-lg sm:text-xl mt-1 truncate font-variant-numeric tabular-nums">{value}</p>
  </div>
);

/* ============================================================ */
/* SUGGESTIONS                                                  */
/* ============================================================ */
const SuggestionsBanner = ({ suggestions, onPickAll, onPickOne }) => {
  const total = suggestions.reduce((acc, s) => acc + (s.total || s.unit_price || 0), 0);
  return (
    <div className="rounded-2xl border border-violet-200 dark:border-violet-900/40 bg-gradient-to-br from-violet-50/70 to-cyan-50/70 dark:from-violet-950/30 dark:to-cyan-950/30 p-4" data-testid="billing-suggestions">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="w-9 h-9 rounded-2xl bg-gradient-to-br from-violet-500 to-cyan-500 flex items-center justify-center flex-shrink-0 shadow">
          <Sparkles className="w-4 h-4 text-white" />
        </div>
        <div className="flex-1 min-w-[180px]">
          <p className="text-[10px] font-bold uppercase tracking-wider text-violet-700 dark:text-violet-300">Prestazioni non fatturate</p>
          <p className="text-sm font-bold text-slate-900 dark:text-white mt-0.5">
            {suggestions.length} {suggestions.length === 1 ? "prestazione" : "prestazioni"} per <span className="font-mono">{fmtEUR(total)}</span>
          </p>
        </div>
        <Button
          size="sm"
          onClick={onPickAll}
          className="rounded-full bg-violet-600 hover:bg-violet-700 text-white text-xs"
          data-testid="billing-suggestions-add-all"
        >
          <Plus className="w-3.5 h-3.5 mr-1" /> Crea ricevuta
        </Button>
      </div>
      <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-1.5">
        {suggestions.slice(0, 4).map((s, i) => (
          <li
            key={s.appointment_id || i}
            className="flex items-center justify-between gap-2 text-xs px-2.5 py-1.5 rounded-lg bg-white/70 dark:bg-white/5 border border-violet-100 dark:border-white/10"
          >
            <div className="min-w-0">
              <p className="font-semibold text-slate-700 dark:text-slate-200 truncate">{s.description}</p>
              <p className="text-[10px] text-slate-400">{fmtDate(s.date)}</p>
            </div>
            <button
              onClick={() => onPickOne(s)}
              className="font-mono font-bold text-violet-600 dark:text-violet-300 hover:underline whitespace-nowrap"
              data-testid={`billing-suggestion-pick-${i}`}
            >
              {fmtEUR(s.unit_price)} <ChevronRight className="inline w-3 h-3" />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

/* ============================================================ */
/* PAYMENTS LIST                                                */
/* ============================================================ */
const PaymentsList = ({ payments, methods, onTopUp, onChanged }) => {
  if (payments.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-slate-200 dark:border-white/10 p-10 text-center" data-testid="billing-empty">
        <div className="mx-auto w-10 h-10 rounded-2xl bg-gradient-to-br from-slate-100 to-slate-50 dark:from-white/10 dark:to-white/5 flex items-center justify-center mb-2">
          <Receipt className="w-5 h-5 text-slate-400" />
        </div>
        <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">Nessun pagamento registrato</p>
        <p className="text-xs text-slate-400 mt-1">Crea la prima ricevuta con il pulsante "Nuovo pagamento".</p>
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="billing-payments-list">
      {payments.map(p => (
        <PaymentRow key={p.payment_id} p={p} onTopUp={onTopUp} onChanged={onChanged} />
      ))}
    </div>
  );
};

const PaymentRow = ({ p, onTopUp, onChanged }) => {
  const st = STATUS[p.status] || STATUS.pending;
  const Icon = st.icon;
  const MethodIcon = METHOD_ICONS[p.method] || Wallet;
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  const downloadPdf = async () => {
    setBusy(true);
    try {
      const r = await axios.get(`${API}/dental/billing/payments/${p.payment_id}/pdf`, {
        withCredentials: true, responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `ricevuta-${(p.receipt_code || "").replace("/", "-")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF scaricato");
    } catch {
      toast.error("Errore download PDF");
    } finally {
      setBusy(false);
    }
  };

  const voidIt = async () => {
    if (!window.confirm("Annullare questa ricevuta? L'operazione resta tracciata.")) return;
    try {
      await axios.post(`${API}/dental/billing/payments/${p.payment_id}/void`, {}, { withCredentials: true });
      toast.success("Ricevuta annullata");
      onChanged?.();
    } catch {
      toast.error("Errore annullamento");
    }
  };

  return (
    <div
      className="group rounded-2xl border border-slate-200/60 dark:border-white/10 bg-white dark:bg-slate-900/40 overflow-hidden transition-all hover:border-violet-300"
      data-testid={`billing-row-${p.payment_id}`}
    >
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-3 p-3 text-left"
      >
        <div className={`w-1.5 self-stretch rounded-full ${st.dot}`} />
        <div className="w-9 h-9 rounded-xl bg-slate-100 dark:bg-white/5 flex items-center justify-center flex-shrink-0">
          <MethodIcon className="w-4 h-4 text-slate-500" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-bold text-slate-900 dark:text-white text-sm">{p.receipt_code}</p>
            <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full ${st.cls}`}>
              <Icon className="w-2.5 h-2.5" /> {st.label}
            </span>
          </div>
          <p className="text-[11px] text-slate-500 truncate">
            {(p.line_items || []).map(l => l.description).join(" · ") || "—"}
          </p>
        </div>
        <div className="text-right flex-shrink-0">
          <p className="font-black text-slate-900 dark:text-white text-base font-variant-numeric tabular-nums">{fmtEUR(p.amount_total)}</p>
          <p className="text-[10px] text-slate-400">{fmtDate(p.paid_at || p.created_at)}</p>
        </div>
      </button>

      {open && (
        <div className="px-3 pb-3 pt-1 border-t border-slate-100 dark:border-white/5 space-y-3">
          {/* Line items */}
          <table className="w-full text-xs" data-testid={`billing-row-lines-${p.payment_id}`}>
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">
                <th className="text-left py-1">Descrizione</th>
                <th className="text-right">Qtà</th>
                <th className="text-right">Prezzo</th>
                <th className="text-right">Totale</th>
              </tr>
            </thead>
            <tbody>
              {(p.line_items || []).map((li, i) => (
                <tr key={i} className="border-t border-slate-100 dark:border-white/5">
                  <td className="py-1.5">
                    <span className="font-medium text-slate-800 dark:text-slate-200">{li.description}</span>
                    {li.tooth_codes?.length > 0 && (
                      <span className="ml-1.5 text-[10px] font-bold text-violet-500">denti {li.tooth_codes.join(", ")}</span>
                    )}
                  </td>
                  <td className="text-right">{li.qty}</td>
                  <td className="text-right font-mono">{fmtEUR(li.unit_price)}</td>
                  <td className="text-right font-mono font-bold">{fmtEUR(li.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Footer chips & actions */}
          <div className="flex items-center justify-between gap-2 flex-wrap text-xs">
            <div className="flex items-center gap-2 text-slate-500">
              {p.status === "partial" && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-200">
                  Pagato {fmtEUR(p.amount_paid)} · Residuo {fmtEUR(p.amount_total - p.amount_paid)}
                </span>
              )}
              {p.notes && <span className="text-[10px] italic text-slate-400">"{p.notes}"</span>}
            </div>
            <div className="flex items-center gap-1.5">
              {p.status === "partial" && (
                <Button
                  size="sm" variant="outline"
                  onClick={() => onTopUp(p)}
                  className="h-7 rounded-full text-[11px]"
                  data-testid={`billing-topup-btn-${p.payment_id}`}
                >
                  <Plus className="w-3 h-3 mr-1" /> Saldo
                </Button>
              )}
              <Button
                size="sm" variant="outline"
                onClick={downloadPdf}
                disabled={busy}
                className="h-7 rounded-full text-[11px]"
                data-testid={`billing-pdf-btn-${p.payment_id}`}
              >
                {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
                <span className="ml-1">PDF</span>
              </Button>
              {p.status !== "voided" && (
                <Button
                  size="sm" variant="ghost"
                  onClick={voidIt}
                  className="h-7 rounded-full text-[11px] text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/20"
                  data-testid={`billing-void-btn-${p.payment_id}`}
                >
                  <Ban className="w-3 h-3" />
                </Button>
              )}
            </div>
          </div>

          {/* Fiscal status */}
          {p.fiscal && (
            <div className="text-[10px] text-slate-400 inline-flex items-center gap-1">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-slate-300" />
              Provider fiscale: <strong className="text-slate-500">{p.fiscal.provider}</strong>
              · stato: <strong className="text-slate-500">{p.fiscal.status}</strong>
            </div>
          )}

          {/* Attachments */}
          <AttachmentsZone payment={p} onChanged={onChanged} />
        </div>
      )}
    </div>
  );
};

/* ============================================================ */
/* ATTACHMENTS                                                  */
/* ============================================================ */
const AttachmentsZone = ({ payment, onChanged }) => {
  const [uploading, setUploading] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File troppo grande (max 10MB)");
      return;
    }
    setUploading(true);
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await axios.post(`${API}/dental/billing/payments/${payment.payment_id}/attachments`, {
          name: file.name,
          mime: file.type || "application/octet-stream",
          data_url: reader.result,
        }, { withCredentials: true });
        toast.success("Allegato caricato");
        onChanged?.();
      } catch (err) {
        toast.error(err.response?.data?.detail || "Errore upload");
      } finally {
        setUploading(false);
      }
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Allegati</p>
        <label className="inline-flex items-center gap-1 text-[10px] font-bold text-violet-600 hover:text-violet-700 cursor-pointer">
          {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
          {uploading ? "Caricamento…" : "Aggiungi file"}
          <input
            type="file"
            className="sr-only"
            onChange={(e) => upload(e.target.files?.[0])}
            data-testid={`billing-attach-input-${payment.payment_id}`}
            disabled={uploading}
          />
        </label>
      </div>
      {(payment.attachments || []).length === 0 ? (
        <p className="text-[10px] text-slate-400">Nessun allegato</p>
      ) : (
        <ul className="space-y-1">
          {payment.attachments.map(a => (
            <li key={a.attachment_id} className="flex items-center justify-between gap-2 text-[11px] px-2 py-1 rounded-lg bg-slate-50 dark:bg-white/5">
              <span className="inline-flex items-center gap-1.5 min-w-0">
                <Paperclip className="w-3 h-3 flex-shrink-0 text-slate-400" />
                <span className="truncate">{a.name}</span>
              </span>
              <a
                href={`${API}${a.url.replace(/^\/api/, "")}`}
                target="_blank" rel="noreferrer"
                className="inline-flex items-center text-violet-600 hover:text-violet-700"
              >
                <ExternalLink className="w-3 h-3" />
              </a>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

/* ============================================================ */
/* NEW PAYMENT DIALOG                                           */
/* ============================================================ */
const blankLine = () => ({ description: "", qty: 1, unit_price: 0 });

const NewPaymentDialog = ({ open, onOpenChange, clientId, methods, seedItems, onCreated, creating, setCreating }) => {
  const [lines, setLines]     = useState([blankLine()]);
  const [method, setMethod]   = useState("cash");
  const [paidNow, setPaidNow] = useState(true);
  const [partialAmt, setPartialAmt] = useState("");
  const [notes, setNotes]     = useState("");
  const [dueAt, setDueAt]     = useState("");

  useEffect(() => {
    if (open) {
      const seeded = (seedItems || []).map(s => ({
        description: s.description,
        qty: s.qty || 1,
        unit_price: s.unit_price || 0,
        appointment_id: s.appointment_id || null,
        service_id: s.service_id || null,
      }));
      setLines(seeded.length ? seeded : [blankLine()]);
      setMethod("cash");
      setPaidNow(true);
      setPartialAmt("");
      setNotes("");
      setDueAt("");
    }
  }, [open, seedItems]);

  const total = useMemo(
    () => lines.reduce((acc, l) => acc + ((Number(l.qty) || 1) * (Number(l.unit_price) || 0)), 0),
    [lines]
  );

  const updateLine = (idx, patch) => setLines(arr => arr.map((l, i) => i === idx ? { ...l, ...patch } : l));
  const addLine = () => setLines(a => [...a, blankLine()]);
  const removeLine = (idx) => setLines(a => a.length === 1 ? a : a.filter((_, i) => i !== idx));

  const submit = async () => {
    const valid = lines.filter(l => l.description.trim() && (Number(l.unit_price) > 0 || Number(l.qty) > 0));
    if (valid.length === 0) {
      toast.error("Inserisci almeno una prestazione con prezzo");
      return;
    }
    const amount_paid = paidNow ? total : (Number(partialAmt) || 0);
    setCreating(true);
    try {
      await axios.post(`${API}/dental/billing/payments`, {
        client_id: clientId,
        method,
        amount_paid,
        line_items: valid.map(l => ({
          description: l.description.trim(),
          qty: Number(l.qty) || 1,
          unit_price: Number(l.unit_price) || 0,
          appointment_id: l.appointment_id || null,
          service_id: l.service_id || null,
        })),
        notes: notes.trim() || null,
        due_at: dueAt || null,
      }, { withCredentials: true });
      toast.success("Ricevuta creata");
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore creazione");
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="billing-new-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-xl bg-gradient-to-br from-violet-500 to-cyan-500 flex items-center justify-center">
              <Receipt className="w-3.5 h-3.5 text-white" />
            </div>
            Nuova ricevuta
          </DialogTitle>
          <DialogDescription>
            Aggiungi le prestazioni da registrare. Il numero ricevuta è progressivo per anno.
          </DialogDescription>
        </DialogHeader>

        {/* LINE ITEMS */}
        <div className="space-y-2">
          {lines.map((l, idx) => (
            <div key={idx} className="grid grid-cols-12 gap-1.5 items-start" data-testid={`billing-line-${idx}`}>
              <Input
                placeholder="Prestazione"
                value={l.description}
                onChange={e => updateLine(idx, { description: e.target.value })}
                className="col-span-6 h-9 text-sm"
                data-testid={`billing-line-desc-${idx}`}
              />
              <Input
                type="number" min="1" placeholder="Qtà"
                value={l.qty}
                onChange={e => updateLine(idx, { qty: e.target.value })}
                className="col-span-2 h-9 text-sm"
                data-testid={`billing-line-qty-${idx}`}
              />
              <Input
                type="number" step="0.01" min="0" placeholder="€"
                value={l.unit_price}
                onChange={e => updateLine(idx, { unit_price: e.target.value })}
                className="col-span-3 h-9 text-sm font-mono"
                data-testid={`billing-line-price-${idx}`}
              />
              <button
                onClick={() => removeLine(idx)}
                className="col-span-1 h-9 rounded-md text-slate-400 hover:text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/20 inline-flex items-center justify-center"
                data-testid={`billing-line-remove-${idx}`}
                aria-label="Rimuovi riga"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
          <Button
            size="sm" variant="ghost"
            onClick={addLine}
            className="rounded-full text-xs"
            data-testid="billing-line-add"
          >
            <Plus className="w-3 h-3 mr-1" /> Aggiungi riga
          </Button>
        </div>

        {/* TOTAL */}
        <div className="rounded-2xl bg-gradient-to-br from-violet-50 to-cyan-50 dark:from-violet-950/40 dark:to-cyan-950/40 p-3 flex items-center justify-between" data-testid="billing-total-box">
          <span className="text-xs font-bold uppercase tracking-wider text-violet-700 dark:text-violet-300">Totale</span>
          <span className="font-black text-2xl font-variant-numeric tabular-nums text-slate-900 dark:text-white">{fmtEUR(total)}</span>
        </div>

        {/* PAYMENT DETAILS */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Metodo</label>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger className="h-9" data-testid="billing-method-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {methods.map(m => (
                  <SelectItem key={m.key} value={m.key}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Pagamento</label>
            <div className="flex gap-1 p-0.5 rounded-lg border border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-white/5 h-9">
              <button
                onClick={() => setPaidNow(true)}
                className={`flex-1 text-xs font-bold rounded-md transition-all ${paidNow ? "bg-white dark:bg-slate-800 shadow text-slate-900 dark:text-white" : "text-slate-500"}`}
                data-testid="billing-paid-now"
              >Saldato</button>
              <button
                onClick={() => setPaidNow(false)}
                className={`flex-1 text-xs font-bold rounded-md transition-all ${!paidNow ? "bg-white dark:bg-slate-800 shadow text-slate-900 dark:text-white" : "text-slate-500"}`}
                data-testid="billing-paid-partial"
              >Parziale/Sospeso</button>
            </div>
          </div>
          {!paidNow && (
            <>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Anticipo ricevuto (€)</label>
                <Input
                  type="number" step="0.01" min="0" max={total}
                  value={partialAmt}
                  onChange={e => setPartialAmt(e.target.value)}
                  placeholder="0 = in sospeso"
                  className="h-9 font-mono"
                  data-testid="billing-partial-amt"
                />
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Scadenza saldo</label>
                <Input
                  type="date"
                  value={dueAt}
                  onChange={e => setDueAt(e.target.value)}
                  className="h-9"
                  data-testid="billing-due-at"
                />
              </div>
            </>
          )}
        </div>

        <Textarea
          placeholder="Note (opzionali)"
          value={notes}
          onChange={e => setNotes(e.target.value)}
          className="text-sm min-h-[60px]"
          data-testid="billing-notes"
        />

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={creating}>Annulla</Button>
          <Button
            onClick={submit}
            disabled={creating || total <= 0}
            className="bg-gradient-to-r from-violet-600 to-cyan-500 hover:opacity-90 text-white"
            data-testid="billing-create-btn"
          >
            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Receipt className="w-4 h-4 mr-1" />}
            {creating ? "Creazione…" : "Crea ricevuta"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* ============================================================ */
/* TOP-UP DIALOG (partial -> paid)                              */
/* ============================================================ */
const TopUpDialog = ({ payment, methods, onClose, onSaved }) => {
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("cash");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (payment) {
      setAmount((payment.amount_total - payment.amount_paid).toFixed(2));
      setMethod(payment.method || "cash");
    }
  }, [payment]);

  if (!payment) return null;
  const residual = payment.amount_total - payment.amount_paid;

  const submit = async () => {
    const add = Number(amount) || 0;
    const newPaid = Math.min(payment.amount_total, payment.amount_paid + add);
    setSaving(true);
    try {
      await axios.patch(`${API}/dental/billing/payments/${payment.payment_id}`, {
        amount_paid: newPaid,
        method,
        paid_at: new Date().toISOString(),
      }, { withCredentials: true });
      toast.success("Saldo aggiornato");
      onSaved();
    } catch {
      toast.error("Errore aggiornamento");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!payment} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="billing-topup-dialog">
        <DialogHeader>
          <DialogTitle>Saldo ricevuta {payment.receipt_code}</DialogTitle>
          <DialogDescription>
            Residuo da incassare: <strong>{fmtEUR(residual)}</strong>
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Importo incassato (€)</label>
            <Input
              type="number" step="0.01" min="0" max={residual}
              value={amount}
              onChange={e => setAmount(e.target.value)}
              className="font-mono"
              data-testid="billing-topup-amt"
            />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Metodo</label>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger data-testid="billing-topup-method"><SelectValue /></SelectTrigger>
              <SelectContent>
                {methods.map(m => <SelectItem key={m.key} value={m.key}>{m.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Annulla</Button>
          <Button
            onClick={submit}
            disabled={saving}
            className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white"
            data-testid="billing-topup-confirm"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
            Conferma saldo
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BillingTab;
